/**
 * Auto Approve Delivery Appeal section.
 *
 * CR-4423 introduced the YES/NO toggle. CR-5885 restored the legacy behaviour
 * where the customer can configure the number of days an appeal stays pending
 * before being auto-approved (default 5). When the toggle is OFF, appeals
 * follow the standard manual approval workflow.
 */

import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Label } from '../../ui/label';
import { Input } from '../../ui/input';
import { Switch } from '../../ui/switch';
import { Loader2, ShieldCheck } from 'lucide-react';
import { platformSettingsAPI } from '../../../lib/api';
import { toast } from 'sonner';

const DEFAULT_DAYS = 5;
const MIN_DAYS = 1;
const MAX_DAYS = 365;

export function AutoApproveDeliveryAppealSection() {
  const [loading, setLoading] = useState(true);
  const [enabled, setEnabled] = useState(false);
  const [days, setDays] = useState<number>(DEFAULT_DAYS);
  // Track the input as a string so the user can clear it while typing without
  // the field snapping back to a number. Validation runs on blur / save.
  const [daysInput, setDaysInput] = useState<string>(String(DEFAULT_DAYS));
  const [savingDays, setSavingDays] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await platformSettingsAPI.getSettings();
        setEnabled(!!data.auto_approve_delivery_appeal);
        const value = Number(data.auto_approve_delivery_appeal_days ?? DEFAULT_DAYS);
        const safe = Number.isFinite(value) && value >= MIN_DAYS ? value : DEFAULT_DAYS;
        setDays(safe);
        setDaysInput(String(safe));
      } catch (err: unknown) {
        console.error('Error loading auto-approve setting:', err);
        toast.error('Failed to load Auto Approve Delivery Appeal setting');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const handleToggle = async (checked: boolean) => {
    const previous = enabled;
    setEnabled(checked);
    try {
      await platformSettingsAPI.patchSettings({ auto_approve_delivery_appeal: checked });
      toast.success(`Auto Approve Delivery Appeal: ${checked ? 'YES' : 'NO'}`);
    } catch (err: unknown) {
      console.error('Error saving auto-approve setting:', err);
      toast.error('Failed to save Auto Approve Delivery Appeal setting');
      setEnabled(previous);
    }
  };

  const commitDays = async () => {
    const parsed = Number.parseInt(daysInput, 10);
    if (!Number.isFinite(parsed) || parsed < MIN_DAYS || parsed > MAX_DAYS) {
      toast.error(`Enter a whole number between ${MIN_DAYS} and ${MAX_DAYS} days`);
      setDaysInput(String(days));
      return;
    }
    if (parsed === days) return;
    const previous = days;
    setDays(parsed);
    setDaysInput(String(parsed));
    setSavingDays(true);
    try {
      await platformSettingsAPI.patchSettings({ auto_approve_delivery_appeal_days: parsed });
      toast.success(`Delivery appeals will auto-approve after ${parsed} day${parsed === 1 ? '' : 's'}`);
    } catch (err: unknown) {
      console.error('Error saving auto-approve days:', err);
      toast.error('Failed to save auto-approval delay');
      setDays(previous);
      setDaysInput(String(previous));
    } finally {
      setSavingDays(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-10">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <ShieldCheck className="h-5 w-5" />
          Auto Approve Delivery Appeal
        </CardTitle>
        <p className="text-sm text-muted-foreground mt-1">
          When enabled, delivery appeals are automatically approved after the configured number of days.
          When disabled, appeals follow the standard manual approval workflow.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center justify-between rounded-md border border-input bg-background px-4 py-3">
          <div className="space-y-1 pr-3">
            <Label htmlFor="auto-approve-delivery-appeal" className="text-sm font-medium">
              Auto Approve Delivery Appeals
            </Label>
            <p className="text-xs text-muted-foreground">
              {enabled
                ? `YES — appeals auto-approve after ${days} day${days === 1 ? '' : 's'}.`
                : 'NO — appeals require manual approval.'}
            </p>
          </div>
          <Switch
            id="auto-approve-delivery-appeal"
            checked={enabled}
            onCheckedChange={handleToggle}
          />
        </div>

        {enabled && (
          <div className="rounded-md border border-input bg-background px-4 py-3">
            <Label htmlFor="auto-approve-delivery-appeal-days" className="flex flex-wrap items-center gap-2 text-sm font-medium">
              <span>Delivery appeal is automatically approved after</span>
              <Input
                id="auto-approve-delivery-appeal-days"
                type="number"
                inputMode="numeric"
                min={MIN_DAYS}
                max={MAX_DAYS}
                step={1}
                className="h-9 w-20 text-center"
                value={daysInput}
                onChange={(e) => setDaysInput(e.target.value)}
                onBlur={commitDays}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    (e.target as HTMLInputElement).blur();
                  }
                }}
                disabled={savingDays}
                aria-describedby="auto-approve-delivery-appeal-days-help"
              />
              <span>days</span>
              {savingDays && <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />}
            </Label>
            <p id="auto-approve-delivery-appeal-days-help" className="mt-2 text-xs text-muted-foreground">
              Whole number between {MIN_DAYS} and {MAX_DAYS}. Default is {DEFAULT_DAYS}. Each auto-approval is recorded in the appeal audit trail.
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
